import { InteractionStatus } from "@azure/msal-browser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { createTicketsClient } from "../client/api";
import { getMsalAuthToken } from "./getMsalAuthToken";

const performHeartbeat = async (getAuthToken: () => Promise<string | void>) => {
  const ticketsClient = await createTicketsClient(getAuthToken);

  await ticketsClient.heartbeat();
};

enum MsalAuthProcessStage {
  Initial,
  MsalInitialized,
  AuthRedirectionsComplete,
  AuthTokenRetrieved,
  Ready,
}

const useGetAuthToken = (): (() => Promise<string | void>) | undefined => {
  const [msalAuthProcessStage, setMsalAuthProcessStage] = useState(
    MsalAuthProcessStage.Initial
  );

  const { instance, accounts, inProgress: msalInProgress } = useMsal();

  const account =
    accounts == null || accounts.length === 0 ? undefined : accounts[0];

  useEffect(() => {
    if (!instance) return;
    if (msalAuthProcessStage !== MsalAuthProcessStage.Initial) return;

    instance.initialize().then(() => {
      setMsalAuthProcessStage((currentAuthProcessStage) => {
        if (currentAuthProcessStage === MsalAuthProcessStage.Initial) {
          return MsalAuthProcessStage.MsalInitialized;
        } else {
          return currentAuthProcessStage;
        }
      });
    });
  }, [instance, msalAuthProcessStage]);

  useEffect(() => {
    if (msalAuthProcessStage !== MsalAuthProcessStage.MsalInitialized) return;
    if (msalInProgress !== InteractionStatus.None) return;

    setMsalAuthProcessStage((currentAuthProcessStage) => {
      if (currentAuthProcessStage === MsalAuthProcessStage.MsalInitialized) {
        return MsalAuthProcessStage.AuthRedirectionsComplete;
      } else {
        return currentAuthProcessStage;
      }
    });
  }, [msalAuthProcessStage, msalInProgress]);

  useEffect(() => {
    if (!instance) return;
    if (msalAuthProcessStage !== MsalAuthProcessStage.AuthRedirectionsComplete)
      return;

    getMsalAuthToken(account, instance).then(() => {
      setMsalAuthProcessStage((currentAuthProcessStage) => {
        if (
          currentAuthProcessStage ===
          MsalAuthProcessStage.AuthRedirectionsComplete
        ) {
          return MsalAuthProcessStage.AuthTokenRetrieved;
        } else {
          return currentAuthProcessStage;
        }
      });
    });
  }, [msalAuthProcessStage, instance, account]);

  useEffect(() => {
    if (!account) return;
    if (!instance) return;
    if (msalAuthProcessStage !== MsalAuthProcessStage.AuthTokenRetrieved)
      return;

    performHeartbeat(() => getMsalAuthToken(account, instance)).then(() => {
      setMsalAuthProcessStage((currentAuthProcessStage) => {
        if (
          currentAuthProcessStage === MsalAuthProcessStage.AuthTokenRetrieved
        ) {
          return MsalAuthProcessStage.Ready;
        } else {
          return currentAuthProcessStage;
        }
      });
    });
  }, [msalAuthProcessStage, account, instance, accounts]);

  const getAuthToken = useCallback(() => {
    return getMsalAuthToken(account, instance);
  }, [account, instance]);

  const getAuthTokenFunction = useMemo(() => {
    if (msalAuthProcessStage !== MsalAuthProcessStage.Ready) return;

    return getAuthToken;
  }, [getAuthToken, msalAuthProcessStage]);

  return getAuthTokenFunction;
};

export default useGetAuthToken;
